import { Suspense } from 'react';

import { TFormalOffer } from '@helpers/FormalOffers/formalOffers.types';
import { FormattedCurrency } from '@iwIntlProvider/FormattedCurrency';
import { fetchPutFormalOffersCreditFacilityConfiguration } from '@iwoca/lapi-client/edge';
import { Button, Card, Pill, Text, Tooltip } from '@iwoca/orion';
import { maintainStateKey } from '@iwRouter/LocationHelper';
import { useMutation } from '@tanstack/react-query';
import { Skeleton } from '@ui';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router-dom';

import { ViewPaymentPlan } from './ViewPaymentPlan';
import { ErrorBoundary } from '../../../../errorBoundary/ErrorBoundary';
import { useStateKey } from '../../../../iwQueryClient/useStateKey';
import FallbackErrorModal from '../../../modals/fallbackErrorModal/FallbackErrorModal';
import { Rate } from '../../../ui/rate';
import {
    ORIGINAL_RATE_DISPLAY,
    useGetDiscountedPrice
} from '../useGetDiscountedPrice';

export const CashlessOfferDetails = ({ offer }: { offer: TFormalOffer }) => {
    return (
        <ErrorBoundary
            fallback={(error) => (
                <Card data-testid="rcf-offer-details-error" className="p-xl">
                    <FallbackErrorModal error={error} />
                </Card>
            )}
        >
            <Suspense
                fallback={
                    <Card
                        data-testid="rcf-offer-details-loading"
                        className="space-y-xl p-xl"
                    >
                        <Skeleton className="h-[25px] w-full" />
                        <Skeleton className="h-[25px] w-full" />
                        <Skeleton className="h-[25px] w-full" />
                        <Skeleton className="h-[25px] w-full" />
                        <Skeleton className="h-[25px] w-full" />
                    </Card>
                }
            >
                <CashlessOfferDetailsContent offer={offer} />
            </Suspense>
        </ErrorBoundary>
    );
};

const CashlessOfferDetailsContent = ({ offer }: { offer: TFormalOffer }) => {
    const history = useHistory();
    const stateKey = useStateKey();

    const { hasDiscountedPricing } = useGetDiscountedPrice({
        enabled: true,
        formalOffer: offer
    });

    const createOfferConfigurationMutation = useMutation({
        mutationKey: ['createOfferConfiguration'],
        mutationFn: ({
            offerId,
            drawdownAmount
        }: {
            offerId: string;
            drawdownAmount: number;
        }) =>
            fetchPutFormalOffersCreditFacilityConfiguration({
                stateKey,
                offerId,
                body: {
                    data: {
                        cash_amount: drawdownAmount
                    }
                }
            })
    });

    const creditLimit = offer.max_amount!;
    const interestFreePeriodMonths = offer.interest_only_repayments;
    const fixedFee =
        offer.proposed_access_period_details!
            .access_period_opening_fee_max_amount!;
    const interestRatePer30Days = offer.interest_rates![0].rate!;

    const goToFundingRequirements = async () => {
        await createOfferConfigurationMutation.mutateAsync({
            offerId: offer.offer_id!,
            drawdownAmount: 0
        });

        history.push(
            maintainStateKey({
                pathname: '/funding-requirements/',
                search: `?offerId=${offer.offer_id!}&amount=0`
            })
        );
    };

    return (
        <Card className="p-xl">
            <div className="w-full space-y-xl">
                <Text className="text-l font-bold">
                    <FormattedMessage
                        id="_.rcf_approved_view.cashless.offer_details_title"
                        defaultMessage="Your Offer Details"
                    />
                </Text>
                <div className="flex items-center justify-between">
                    <Text>
                        <FormattedMessage
                            id="_.rcf_approved_view.cashless.offer_details_credit_line"
                            defaultMessage="Available Credit Line"
                        />
                    </Text>
                    <div className="flex items-center gap-s">
                        <Text
                            className="text-l font-bold"
                            data-testid="offer-details-credit-limit"
                        >
                            <FormattedCurrency value={creditLimit} />
                        </Text>
                        <Tooltip
                            data-testid="offer-details-credit-line-tooltip"
                            // @ts-expect-error
                            content={
                                <FormattedMessage
                                    id="_.rcf_approved_view.cashless.offer_details_credit_line_tooltip"
                                    defaultMessage="The maximum amount you can withdraw at any time during your 12-month access period."
                                />
                            }
                        />
                    </div>
                </div>
                <div className="flex items-center justify-between">
                    <Text>
                        <FormattedMessage
                            id="_.rcf_approved_view.cashless.offer_details_interest_free_period"
                            defaultMessage="Interest-Free Period"
                        />
                    </Text>
                    <div className="flex items-center gap-s">
                        <Text className="text-l font-bold">
                            <FormattedMessage
                                id="_.rcf_approved_view.cashless.offer_details_interest_free_period_months"
                                defaultMessage="{months} Months"
                                values={{ months: interestFreePeriodMonths }}
                            />
                        </Text>
                        <Tooltip
                            data-testid="offer-details-interest-free-period-tooltip"
                            // @ts-expect-error
                            content={
                                <FormattedMessage
                                    id="_.rcf_approved_view.cashless.offer_details_interest_free_period_tooltip"
                                    defaultMessage="During this period, you will only pay interest on the outstanding monthly amount. Extension possible."
                                />
                            }
                        />
                    </div>
                </div>
                <div className="flex items-center justify-between">
                    <Text>
                        <FormattedMessage
                            id="_.rcf_approved_view.cashless.offer_details_one_time_fee"
                            defaultMessage="One-time Fee"
                        />
                    </Text>
                    <div className="flex items-center gap-s">
                        <Text className="text-l font-bold">
                            <FormattedCurrency value={fixedFee} />
                        </Text>
                        <Tooltip
                            data-testid="offer-details-one-time-fee-tooltip"
                            // @ts-expect-error
                            content={
                                <FormattedMessage
                                    id="_.rcf_approved_view.cashless.offer_details_one_time_fee_tooltip"
                                    defaultMessage="A one-off fee charged when you activate your credit line. It will be deducted with your first repayment."
                                />
                            }
                        />
                    </div>
                </div>
                <div className="flex items-center justify-between">
                    <Text>
                        <FormattedMessage
                            id="_.rcf_approved_view.cashless.offer_details_interest_rate"
                            defaultMessage="Interest Rate per 30 Days"
                        />
                    </Text>
                    <div className="flex items-center gap-s">
                        <div className="text-right text-l font-bold">
                            {hasDiscountedPricing && (
                                <>
                                    <div>
                                        <Pill>
                                            <FormattedMessage
                                                id="_.rcf_approved_view.offer_details_discounted_offer"
                                                defaultMessage="Our offer for you:"
                                            />
                                        </Pill>
                                    </div>
                                    <span className="font-reg line-through">
                                        {ORIGINAL_RATE_DISPLAY}
                                    </span>{' '}
                                </>
                            )}
                            <Rate value={interestRatePer30Days} />
                        </div>
                        <Tooltip
                            data-testid="offer-details-interest-rate-tooltip"
                            // @ts-expect-error
                            content={
                                <FormattedMessage
                                    id="_.rcf_approved_view.cashless.offer_details_interest_rate_tooltip"
                                    defaultMessage="Interest is only charged on the amount you have withdrawn, not on the full credit line."
                                />
                            }
                        />
                    </div>
                </div>
            </div>

            <div className="my-xl">
                <ViewPaymentPlan drawdownAmount={creditLimit} offer={offer} />
            </div>
            <Button
                onClick={goToFundingRequirements}
                loading={createOfferConfigurationMutation.isLoading}
                variant="success"
                className="w-full"
            >
                <FormattedMessage
                    id="_.rcf_approved_view.cashless.proceed_with_offer"
                    defaultMessage="Proceed with this offer"
                />
            </Button>
        </Card>
    );
};
